import {
  CSVDownloadButton,
  DetectBrowserDevice,
  FeedInAnnualOverview,
  FeedInAnnualOverviewPlaceholder,
  FeedInChart,
} from '@e2m-customer-portal/domains';
import {
  MarketLocation,
  MarketLocationProvider,
  useFeedIn,
  useLocale,
  useSpotPrices,
  useStartOfWeek,
} from '@e2m-customer-portal/hooks';
import { isDefined } from '@e2m-customer-portal/shared';
import { DateTimePicker, ErrorComponent, Loading, PageTitle, Toolbar } from '@e2m-customer-portal/ui';
import { Button, ButtonGroup } from '@mui/material';
import type { Interval } from 'date-fns';
import {
  addDays,
  addMinutes,
  endOfTomorrow,
  isAfter,
  isBefore,
  isSameDay,
  max,
  min,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  startOfYear,
  subDays,
  subYears,
  toDate,
} from 'date-fns';
import { Resolution } from 'generated/graphql';
import { ReactElement, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';
import { mostCompactName } from 'utils/most-compact-name';

// Data is only available up to two years in the past
const EARLIEST_YEAR = new Date().getFullYear() - 2;

interface FeedInPageProps {
  marketLocation: MarketLocation;
  resolution?: Resolution;
}

function byInterval(interval: Interval): (item: { interval: Interval }) => boolean {
  return (item) => {
    return isSameDay(item.interval.start, interval.start) && isSameDay(item.interval.end, interval.end);
  };
}

export function FeedInPage({ marketLocation, resolution }: Readonly<FeedInPageProps>): ReactElement<FeedInPageProps> {
  const isMobile = DetectBrowserDevice() === 'Mobile';
  const { t } = useTranslation();
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();
  const isFifteenMinuteResolution = resolution === Resolution.FIFTEEN_MINUTES;

  const [dataInterval, setDataInterval] = useState<Interval>({
    start: subDays(startOfToday(), 7),
    end: endOfTomorrow(),
  });
  const [data, error] = useFeedIn(
    [marketLocation, marketLocation.linkedMarketLocation]
      .filter(isDefined)
      .map((maLo) => ({ id: maLo.id, type: maLo.type })),
    {
      ...dataInterval,
      end: startOfDay(addDays(dataInterval.end, 1)),
    },
  );
  const { data: spotPriceData } = useSpotPrices({
    interval: {
      start: toDate(dataInterval.start),
      end: toDate(isBefore(dataInterval.end, addMinutes(startOfTomorrow(), 8)) ? dataInterval.end : startOfTomorrow()),
    },
  });

  const DAYS_BACK = isMobile ? [7, 30] : [7, 30, 90];

  const buttons = DAYS_BACK.map((days) => ({
    days,
    label: t('MARKET_LOCATION_FEED_IN_LAST_X_DAYS', { count: days }),
    interval: {
      start: subDays(startOfToday(), days),
      end: endOfTomorrow(),
    },
  }));
  const [selectedButton, setSelectedButton] = useState<{ interval: Interval; days: number } | undefined>(buttons[0]);

  const handleButtonClick = (days: number) => () => {
    const start = subDays(startOfToday(), days);
    const end = endOfTomorrow();
    setSelectedButton(buttons.find((button) => button.days === days));
    if (isBefore(start, dataInterval.start) || isAfter(end, dataInterval.end)) {
      setDataInterval({
        start: min([dataInterval.start, start]),
        end: max([dataInterval.end, end]),
      });
    }
  };

  const handleIntervalChange = (interval: Interval): void => {
    const nextSelected = buttons.find(byInterval(interval));
    if (selectedButton !== nextSelected) {
      setSelectedButton(nextSelected);
    }
  };

  const [annualOverviewYear, setAnnualOverviewYear] = useState(new Date().getFullYear());
  const earliestYear = Math.max(marketLocation.marketingStart?.getFullYear() ?? EARLIEST_YEAR, EARLIEST_YEAR);
  const marketingStartIsInvalid = earliestYear > new Date().getFullYear();

  const marketLocationName =
    marketLocation.externalName && marketLocation.externalName !== marketLocation.name
      ? marketLocation.externalName
      : mostCompactName(
          marketLocation.externalName || marketLocation.name,
          marketLocation.linkedMarketLocation?.externalName || marketLocation.linkedMarketLocation?.name,
        );

  return (
    <div>
      <PageTitle label={t('MASTER_DATA_TAB_PLANT')} title={marketLocationName} />

      <Toolbar
        left={
          <>
            <DateTimePicker
              sx={{ maxWidth: isMobile ? '100%' : '270px' }}
              label={t('MARKET_LOCATION_FEED_IN_DATE_PICKER_LABEL')}
              small
              mode="range"
              pickerProps={{
                dateOnly: true,
                disabled: [{ before: startOfYear(subYears(new Date(), 1)) }, { after: endOfTomorrow() }],
                locale,
                startOfWeek,
                fixedWeeks: true,
                numberOfMonths: 1,
                showWeekNumber: true,
              }}
              onSelect={({ range: interval }) => {
                const nextSelected = buttons.find(byInterval(interval));
                setSelectedButton(nextSelected);
                setDataInterval(interval);
              }}
              required
              range={selectedButton?.interval ?? dataInterval}
            />

            <ButtonGroup sx={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'auto' }}>
              {buttons.map((button) => (
                <Button
                  aria-pressed={button.days === selectedButton?.days}
                  key={button.days}
                  onClick={handleButtonClick(button.days)}
                  variant={button.days === selectedButton?.days ? 'contained' : 'outlined'}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                  {button.label}
                </Button>
              ))}
            </ButtonGroup>
          </>
        }
        right={
          !isMobile && (
            <MarketLocationProvider value={marketLocation}>
              <CSVDownloadButton />
            </MarketLocationProvider>
          )
        }
      />

      <Suspense fallback={<Loading />}>
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <FeedInChart
            marketLocation={marketLocation}
            dataInterval={dataInterval}
            values={data ? new Map([...data].map((feedIn) => [feedIn.marketLocationId, feedIn.values])) : undefined}
            spotPriceData={spotPriceData}
            shownInterval={selectedButton?.interval}
            onChangeShownInterval={handleIntervalChange}
            isFifteenMinuteResolution={isFifteenMinuteResolution}
            isMobile={isMobile}
            error={error}
          />
        </ErrorBoundary>
      </Suspense>
      <div className={marketingStartIsInvalid ? 'relative' : undefined}>
        <div className="flex items-center gap-x-2 py-3 pl-6 pr-4">
          <h3 className="m-0 font-sans text-xl font-semibold" data-testid="annual-overview-title">
            {t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_TITLE', {
              name: marketLocationName,
            })}
          </h3>
          {marketingStartIsInvalid ? (
            <div className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center text-error-50 uppercase z-10">
              <span className="-rotate-12 text-center p-2 rounded-md border-solid border-4 border-error-50 bg-error-10/15 backdrop-blur-md [font-size:clamp(1rem,3rem,5vw)]">
                <Trans components={{ br: <br /> }} values={{ year: marketLocation.marketingStart ?? earliestYear }}>
                  MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_INVALID_MARKETING_START
                </Trans>
              </span>
            </div>
          ) : (
            <select
              className="text-xl"
              value={annualOverviewYear}
              onChange={(event) => setAnnualOverviewYear(Number(event.target.value))}
            >
              {new Array(new Date().getFullYear() + 1 - earliestYear).fill(0).map((_, index) => (
                <option key={`annual-overview-item-${earliestYear + index}`}>{earliestYear + index}</option>
              ))}
            </select>
          )}
        </div>
        <div className={marketingStartIsInvalid ? 'blur-sm' : undefined}>
          <Suspense fallback={<FeedInAnnualOverviewPlaceholder marketLocation={marketLocation} isMobile={isMobile} />}>
            <ErrorBoundary FallbackComponent={ErrorComponent}>
              <FeedInAnnualOverview marketLocation={marketLocation} year={annualOverviewYear} isMobile={isMobile} />
            </ErrorBoundary>
          </Suspense>
        </div>
      </div>
    </div>
  );
}
